import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import type { DateWindow } from './date-window';
import type {
  BackendCampaignType,
  CreateCrossChannelExportParams,
} from '../slice/api';
import {
  type RateLimitErrorResponse,
  rateLimitErrorResponse,
  validationErrorResponse,
} from './export-error-schema';
import type { ExportCampaignRow } from '../components/CrossChannelExportModal/columns';

export function buildExportPayload(
  selectedRows: ExportCampaignRow[],
  dateWindow: DateWindow | null,
  backendType: BackendCampaignType
): CreateCrossChannelExportParams {
  return {
    campaign_type: backendType,
    campaign_reports: selectedRows.map(row => ({
      campaign_id: row.campaignId,
      report_id: row.reportId,
    })),
    ...(dateWindow && {
      report_start_date: dateWindow.start,
      report_end_date: dateWindow.end,
    }),
  };
}

export type ExportErrorResult =
  | { type: 'snackbar'; message: string; variant: 'warning' | 'error' }
  | { type: 'notify'; message: string; consoleMessage: [string, unknown] };

function getRateLimitMessage(parsed: RateLimitErrorResponse): string {
  if (parsed.error === 'concurrent_limit_exceeded') {
    return 'You already have an export in progress. Please wait for it to complete.';
  }
  const hours =
    typeof parsed.windowHours === 'number' ? parsed.windowHours : 24;
  return `You have reached the export limit. Please try again after ${hours} hours.`;
}

export function getExportErrorResult(
  error: FetchBaseQueryError
): ExportErrorResult {
  const data = error.data;

  if (error.status === 429) {
    const parsed = rateLimitErrorResponse.safeParse(data);
    if (parsed.success) {
      return {
        type: 'snackbar',
        message: getRateLimitMessage(parsed.data),
        variant: 'warning',
      };
    }
    return {
      type: 'snackbar',
      message: 'Too many requests. Please try again later.',
      variant: 'warning',
    };
  }

  if (error.status === 400) {
    const parsed = validationErrorResponse.safeParse(data);
    if (parsed.success) {
      return {
        type: 'snackbar',
        message: parsed.data.error,
        variant: 'error',
      };
    }
    return {
      type: 'notify',
      message: 'Export request failed',
      consoleMessage: ['Cross-channel export error', error],
    };
  }

  if (error.status === 403) {
    return {
      type: 'snackbar',
      message: 'You do not have permission to export.',
      variant: 'error',
    };
  }

  return {
    type: 'notify',
    message: 'Export request failed',
    consoleMessage: ['Cross-channel export error', error],
  };
}
